import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, Lock, Share2, ShieldAlert } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { useCan } from '@/lib/permissions'
import type { CaseDetail, CaseStatus } from '../types'

const STATUS_VARIANT: Record<CaseStatus, React.ComponentProps<typeof Badge>['variant']> = {
  open: 'pending',
  in_progress: 'info',
  in_review: 'info',
  pending_info: 'pending',
  escalated: 'critical',
  resolved: 'success',
  closed: 'cancelled',
  fraud_confirmed: 'critical',
  false_positive: 'success',
}

/**
 * Catalogue-aligned investigation header.
 *
 * Top row mirrors the "Investigation Workspace / Case F-2026-8891"
 * mock from the product catalogue — breadcrumb to case number with
 * status chip + SLA countdown, three primary action buttons on the
 * right (Share, Block Account, Disposition Fraud).
 *
 * The action buttons route to existing surfaces — no new backend
 * paths. "Disposition Fraud" jumps to the disposition flow already
 * wired into the case via the existing actions panel; "Block
 * Account" routes to /blocks/new prefilled with the case context
 * (type=customer + customer id + source=case + a reason draft) so
 * the analyst lands on a form that's submittable in one edit
 * rather than a blank slate.
 */
export function InvestigationHeader({ c }: { c: CaseDetail }) {
  const canBlock = useCan('blocks:request')
  const canDispose = useCan('cases:write')
  const sla = useSlaCountdown(c)
  const blockHref = useMemo(() => buildBlockHref(c), [c])

  return (
    <header className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <Link
          to="/cases"
          className="rounded px-1 text-sm text-muted-fg hover:bg-muted hover:text-fg"
        >
          Investigation Workspace
        </Link>
        <ChevronRight size={12} className="text-muted-fg" aria-hidden />
        <h1 className="font-mono text-base font-semibold tabular-nums">
          {c.case_number}
        </h1>
        <Badge variant={STATUS_VARIANT[c.status]} className="capitalize">
          {c.status.replace('_', ' ')}
        </Badge>
        {sla ? (
          <span
            className={
              'rounded-sm border px-2 py-0.5 font-mono text-[0.6875rem] tabular-nums ' +
              (sla.tone === 'breach'
                ? 'border-status-failed/40 bg-status-failed/10 text-status-failed'
                : sla.tone === 'warn'
                  ? 'border-status-pending/40 bg-status-pending/10 text-status-pending'
                  : 'border-border bg-muted text-muted-fg')
            }
            title={`SLA target: ${sla.targetHours}h from open`}
          >
            SLA: {sla.label}
          </span>
        ) : null}
        <span className="font-mono text-xs text-muted-fg">
          {c.alert_count} alert{c.alert_count === 1 ? '' : 's'}
        </span>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void copyShareLink(c.id)}
          title="Copy a deep link to this case"
        >
          <Share2 size={12} className="mr-1" />
          Share
        </Button>
        {canBlock ? (
          <Button asChild type="button" variant="outline" size="sm" title="Open a block request">
            <Link to={blockHref}>
              <Lock size={12} className="mr-1" />
              Block account
            </Link>
          </Button>
        ) : null}
        {canDispose ? (
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => scrollIntoSection('disposition')}
            title="Jump to the disposition / decision panel"
          >
            <ShieldAlert size={12} className="mr-1" />
            Disposition fraud
          </Button>
        ) : null}
      </div>
    </header>
  )
}

// ---- helpers --------------------------------------------------------

// buildBlockHref constructs the /blocks/new URL with prefill params
// that BlockRequestPage reads on mount (type, bank_code, nuban,
// identifier, source, source_reference, reason).
//
// Priority order — most-specific target wins:
//   1. primary_account present + bank_code present → type=account
//      with bank+NUBAN. This is the path for A2A / cash-rail cases
//      where the impacted account is unambiguous.
//   2. primary_customer present → type=customer with the customer
//      id. Falls back here for card / device / IP-only flows where
//      no NUBAN is on the transaction, plus the rare unresolved-bank
//      case (primary_account.bank_code missing).
//   3. Neither → bare /blocks/new. The analyst fills everything by
//      hand. Should be vanishingly rare in practice.
function buildBlockHref(c: CaseDetail): string {
  const reasonHead = `Block requested from case ${c.case_number}`
  const reasonAlerts =
    `${c.alert_count} alert${c.alert_count === 1 ? '' : 's'} on file — see case for evidence.`

  if (c.primary_account?.nuban && c.primary_account.bank_code) {
    const subject = c.primary_customer
      ? `${c.primary_customer.display_name}, ${c.primary_customer.customer_number}`
      : c.primary_account.account_name || c.primary_account.nuban
    const params = new URLSearchParams({
      type: 'account',
      bank_code: c.primary_account.bank_code,
      nuban: c.primary_account.nuban,
      source: 'case',
      source_reference: c.case_number,
      reason: `${reasonHead} (${subject}). ${reasonAlerts}`,
    })
    return `/blocks/new?${params.toString()}`
  }

  if (c.primary_customer) {
    const params = new URLSearchParams({
      type: 'customer',
      identifier: c.primary_customer.id,
      source: 'case',
      source_reference: c.case_number,
      reason:
        `${reasonHead} (${c.primary_customer.display_name}, ` +
        `${c.primary_customer.customer_number}). ${reasonAlerts}`,
    })
    return `/blocks/new?${params.toString()}`
  }

  return '/blocks/new'
}

interface SlaState {
  label: string
  tone: 'ok' | 'warn' | 'breach'
  targetHours: number
}

// useSlaCountdown derives an SLA countdown from the case's created_at
// + a priority-tier-derived target. Catalogue SLA buckets (per the
// Quick Action Settings panel): critical 1h, high 4h, medium 8h,
// low 24h.
function useSlaCountdown(c: CaseDetail): SlaState | null {
  const opened = new Date(c.created_at).getTime()
  if (!Number.isFinite(opened)) return null
  // Map case status → target hours. We don't have a priority on the
  // case directly so fall back to in_review = 1h, open = 4h, others
  // = no SLA. Maps to the catalogue's SLA Escalation defaults.
  const targetHours = slaHours(c)
  if (!targetHours) return null
  const elapsedMs = Date.now() - opened
  const targetMs = targetHours * 60 * 60_000
  const remainingMs = targetMs - elapsedMs
  const tone: SlaState['tone'] =
    remainingMs <= 0 ? 'breach' : remainingMs < 30 * 60_000 ? 'warn' : 'ok'
  return {
    label: tone === 'breach' ? `breached ${formatDuration(-remainingMs)}` : formatDuration(remainingMs),
    tone,
    targetHours,
  }
}

function slaHours(c: CaseDetail): number | null {
  switch (c.status) {
    case 'in_progress':
    case 'in_review':
    case 'escalated':
      return 1
    case 'pending_info':
      return 2
    case 'open':
      return 4
    default:
      return null
  }
}

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 60_000))
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

// copyShareLink writes a link to the case onto the clipboard and
// surfaces a toast — without the toast, the button was a silent
// no-op (clicked → nothing visible). The fallback path uses an
// off-screen <textarea> + execCommand so the affordance still
// works in non-secure contexts (any deployment without HTTPS).
async function copyShareLink(id: string) {
  const url = window.location.origin + `/cases/${id}`
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url)
    } else {
      const ta = document.createElement('textarea')
      ta.value = url
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    toast.success('Case link copied to clipboard', {
      description: url,
      duration: 3500,
    })
  } catch (err) {
    toast.error("Couldn't copy link", {
      description: err instanceof Error ? err.message : 'Clipboard unavailable',
    })
  }
}

function scrollIntoSection(id: string) {
  const el = document.getElementById(id)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}
